/**
 * Editor da apresentação ao cliente (antes "Por que consórcio"), em tela cheia.
 * Abre no modo padrão: a apresentação como o cliente vai ver, pronta para o PDF
 * e o link. "Personalizar" abre os ajustes — cada grupo fechado num resumo, que
 * abre para mudar (Rick, 17/10/2026):
 *   - o plano: o da proposta ou outro com crédito parecido, deste ou de outro
 *     grupo (alternativas.ts), e quais deles entram no comparativo;
 *   - as premissas (juros, mês, ágio, forma de contemplação, reajuste, CDI…);
 *   - as seções que entram.
 * No computador os ajustes ficam ao lado da apresentação; no celular, um de cada vez.
 *
 * O segmento vem da proposta; só quando ela não diz qual é, o editor pergunta.
 * Estado e conta em useEstudoEditor.
 */
import { useEffect, useState } from 'react';
import { useNotify } from 'ra-core';
import { ChevronDown, Download, Eye, Link2, Loader2, SlidersHorizontal, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { formatBRL } from '@/lib/format';
import { NOME_SEGMENTO } from './comparativoPdf';
import { NOME_CENARIO } from './resumoCliente';
import { criarLinkEstudo } from './estudoLink';
import { secoesDisponiveis } from './estudoPaginas';
import { baixarEstudoPdf } from './estudoPdf';
import { EstudoSecoes } from './EstudoTela';
import { LinkDoEstudo } from './LinkDoEstudo';
import { PainelAlternativas } from './PainelAlternativas';
import { PainelPremissas } from './PainelPremissas';
import { useEstudoEditor } from './useEstudoEditor';
import type { ProposalPayload, UserInfo } from './proposal-utils';

export { lerNumero } from './useEstudoEditor';

type Props = {
  /** Proposta a comparar; null fecha o editor. */
  payload: ProposalPayload | null;
  userInfo: UserInfo;
  onClose: () => void;
};

/**
 * Um grupo de ajustes, fechado por padrão: mostra o resumo do que está valendo
 * e abre para mudar (Rick, 17/10/2026 — "um modo padrão e aí você abre e
 * modifica o que quiser, não mostrar sempre para modificar").
 */
const Ajuste = ({
  titulo,
  resumo,
  aberto,
  onAlternar,
  children,
}: {
  titulo: string;
  resumo: React.ReactNode;
  aberto: boolean;
  onAlternar: () => void;
  children: React.ReactNode;
}) => (
  <section className="rounded-lg border">
    <button
      type="button"
      onClick={onAlternar}
      aria-expanded={aberto}
      className="flex min-h-12 w-full items-start gap-3 px-3 py-3 text-left hover:bg-muted/40"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#9A8466]">{titulo}</span>
        {!aberto && <span className="mt-1 block text-sm">{resumo}</span>}
      </span>
      <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-[#354660] dark:text-primary">
        {aberto ? 'Fechar' : 'Alterar'}
        <ChevronDown className={cn('h-4 w-4 transition-transform', aberto && 'rotate-180')} />
      </span>
    </button>
    {aberto && <div className="space-y-3 border-t px-3 py-3">{children}</div>}
  </section>
);

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

export const PorQueConsorcioDialog = ({ payload, userInfo, onClose }: Props) => {
  const notify = useNotify();
  const ed = useEstudoEditor(payload);
  const { estudo } = ed;
  const [gerando, setGerando] = useState(false);
  const [criandoLink, setCriandoLink] = useState(false);
  const [link, setLink] = useState<{ url: string; expiraEm: string } | null>(null);
  // Modo padrão: só a apresentação. "Personalizar" abre os ajustes.
  const [personalizando, setPersonalizando] = useState(false);
  const [aberto, setAberto] = useState<'plano' | 'premissas' | 'secoes' | null>(null);
  const ocupado = gerando || criandoLink;
  const alternar = (g: 'plano' | 'premissas' | 'secoes') => setAberto((a) => (a === g ? null : g));

  // O link guarda a apresentação do momento: mudou qualquer escolha, o link mostrado deixa de valer.
  useEffect(() => setLink(null), [estudo]);
  useEffect(() => {
    setPersonalizando(false);
    setAberto(null);
  }, [payload]);

  const gerarPdf = async () => {
    if (!estudo) return;
    setGerando(true);
    try {
      await baixarEstudoPdf(estudo, userInfo);
    } catch (err) {
      console.error('Por que consórcio: falha ao gerar', err);
      notify('Não foi possível gerar o PDF da apresentação.', { type: 'error' });
    } finally {
      setGerando(false);
    }
  };

  const gerarLink = async () => {
    if (!estudo) return;
    setCriandoLink(true);
    try {
      setLink(await criarLinkEstudo(estudo, userInfo));
    } catch (err) {
      console.error('Por que consórcio: falha ao criar o link', err);
      notify('Não foi possível criar o link da apresentação.', { type: 'error' });
    } finally {
      setCriandoLink(false);
    }
  };

  const semDados = !ed.carregando && ed.segmento && ed.prazo === null;
  // Sem segmento ou com uma escolha inválida não há apresentação: os ajustes abrem sozinhos.
  const ajustes = personalizando || (!ed.carregando && !!ed.ind && (!ed.segmento || !!semDados || (!estudo && !!ed.erro)));
  const ativo = ed.ativo;
  const nomePlano = ativo?.groupNumber ? `Grupo ${ativo.groupNumber}${ativo.categoria ? ` · ${ativo.categoria}` : ''}` : 'Plano da proposta';
  const nComparativo = ed.selecionadas.size;
  const secoes = estudo ? secoesDisponiveis(estudo) : [];
  const nSecoes = secoes.filter((x) => !ed.ocultas.includes(x.id)).length;

  return (
    <Dialog open={payload !== null} onOpenChange={(a) => !a && !ocupado && onClose()}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:max-w-none [&>button:last-child]:hidden">
        {/* Celular: título e fechar numa linha, Gerar link e Gerar PDF juntos na de baixo. */}
        <header className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-base">Apresentação</DialogTitle>
            <DialogDescription className="truncate text-xs">
              {ativo?.clientName ? `${String(ativo.clientName)} · ` : ''}PDF e link idênticos à pré-visualização.
            </DialogDescription>
          </div>
          <div className="order-last flex w-full gap-2 sm:order-none sm:w-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="hidden lg:inline-flex"
              onClick={() => setPersonalizando((p) => !p)}
              aria-pressed={personalizando}
            >
              <SlidersHorizontal className="mr-1 h-4 w-4" />
              {personalizando ? 'Fechar ajustes' : 'Personalizar'}
            </Button>
            <Button type="button" variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={gerarLink} disabled={ocupado || !estudo}>
              {criandoLink ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />}
              Gerar link
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={gerarPdf}
              disabled={ocupado || !estudo}
              className="flex-1 bg-[#354660] text-white hover:bg-[#29374B] sm:flex-none dark:bg-primary dark:text-primary-foreground dark:hover:bg-primary/90"
            >
              {gerando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
              Gerar PDF
            </Button>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={ocupado} aria-label="Fechar">
            <X className="h-4 w-4" />
          </Button>
        </header>

        {/* O link logo abaixo dos botões: no celular, dentro dos ajustes, ele nascia fora da tela. */}
        {link && (
          <div className="border-b px-4 py-3">
            <LinkDoEstudo url={link.url} />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          {ajustes && (
            <aside className="min-h-0 w-full flex-1 space-y-3 overflow-y-auto p-4 lg:w-[420px] lg:flex-none lg:shrink-0 lg:border-r">
              {ed.carregando || !ed.ind ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Buscando as taxas no Banco Central...
                </p>
              ) : !ed.segmento ? (
                <div className="space-y-2 text-sm">
                  <Label>Segmento da proposta</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => ed.setSegmento('veiculo')}>
                      Automóvel
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => ed.setSegmento('imovel')}>
                      Imóvel
                    </Button>
                  </div>
                </div>
              ) : semDados ? (
                <p className="text-sm text-destructive">
                  Esta proposta não tem crédito, prazo e taxa de administração suficientes para o comparativo.
                </p>
              ) : (
                <>
                  <Ajuste
                    titulo="Plano"
                    aberto={aberto === 'plano'}
                    onAlternar={() => alternar('plano')}
                    resumo={
                      <>
                        <span className="block font-semibold">
                          {nomePlano} · {NOME_SEGMENTO[ed.segmento]}
                        </span>
                        {estudo && (
                          <span className="block text-xs text-muted-foreground">
                            {formatBRL(estudo.entrada.credito)} · {estudo.entrada.prazo} meses · taxa de {pct(estudo.entrada.taxaAdminPct)}
                          </span>
                        )}
                        <span className="block text-xs text-muted-foreground">
                          {nComparativo === 0
                            ? 'Sem outros planos no comparativo'
                            : nComparativo === 1
                              ? '+ 1 plano no comparativo'
                              : `+ ${nComparativo} planos no comparativo`}
                        </span>
                      </>
                    }
                  >
                    {estudo?.empate && !estudo.empate.principal.consorcioGanha && (
                      <p className="text-xs text-amber-700">Neste prazo o financiamento sai mais barato. Planos que ganham estão abaixo.</p>
                    )}
                    {ed.trocouPlano && (
                      <div className="flex items-start justify-between gap-2 rounded-md bg-[#F3EDE3] px-3 py-2 text-xs text-[#1F2C40] dark:bg-muted dark:text-foreground">
                        <span>A apresentação usa outro plano. Para vender este, gere a proposta dele no simulador.</span>
                        <Button type="button" size="sm" variant="ghost" className="h-6 shrink-0 px-2 text-xs" onClick={ed.voltarAoOriginal}>
                          <Undo2 className="mr-1 h-3 w-3" /> Voltar
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Outros planos com crédito parecido. Os marcados entram no comparativo da página "Onde o consórcio ganha".
                    </p>
                    <PainelAlternativas
                      alternativas={ed.alternativas}
                      falhou={ed.falhouAlternativas}
                      sistema={ed.sistema}
                      ordem={ed.ordem}
                      onOrdem={ed.setOrdem}
                      selecionadas={ed.selecionadas}
                      onAlternar={ed.alternarSelecao}
                      onUsar={ed.usarAlternativa}
                      usando={ed.usando}
                    />
                  </Ajuste>

                  <Ajuste
                    titulo="Premissas"
                    aberto={aberto === 'premissas'}
                    onAlternar={() => alternar('premissas')}
                    resumo={
                      estudo ? (
                        <span className="block text-xs text-muted-foreground">
                          Financiamento a {pct(estudo.opcoes.jurosAa ?? 0)} ao ano · contemplação no mês {estudo.entrada.mesContemplacao}
                          {ed.temSimulacao ? ` · ${NOME_CENARIO[ed.cenario]}` : ''}
                        </span>
                      ) : (
                        <span className="block text-xs text-destructive">{ed.erro ?? 'Revise as premissas.'}</span>
                      )
                    }
                  >
                    <PainelPremissas ed={ed} />
                  </Ajuste>

                  {estudo && (
                    <Ajuste
                      titulo="Seções"
                      aberto={aberto === 'secoes'}
                      onAlternar={() => alternar('secoes')}
                      resumo={
                        <span className="block text-xs text-muted-foreground">
                          {nSecoes === secoes.length ? 'Todas as seções' : `${nSecoes} de ${secoes.length} seções`}
                        </span>
                      }
                    >
                      <div className="space-y-2 text-sm">
                        {secoes.map((x) => (
                          <label key={x.id} className="flex items-center gap-2">
                            <Checkbox checked={!ed.ocultas.includes(x.id)} onCheckedChange={() => ed.alternarSecao(x.id)} />
                            {x.titulo}
                          </label>
                        ))}
                      </div>
                    </Ajuste>
                  )}
                </>
              )}
            </aside>
          )}

          <div
            className={cn('min-h-0 flex-1 overflow-y-auto bg-[#0F1A24]', ajustes && 'hidden lg:block')}
            role="region"
            aria-label="Pré-visualização da apresentação"
          >
            {estudo ? (
              <EstudoSecoes estudo={estudo} especialista={userInfo} />
            ) : (
              <p className="px-6 py-24 text-center text-sm text-white/70">
                {ed.carregando ? 'Montando a apresentação...' : ed.erro ?? 'Selecione as premissas.'}
              </p>
            )}
          </div>
        </div>

        <div className="border-t p-3 lg:hidden">
          {ajustes ? (
            <Button type="button" className="w-full" onClick={() => setPersonalizando(false)} disabled={!estudo}>
              <Eye className="mr-1 h-4 w-4" /> Ver a apresentação
            </Button>
          ) : (
            <Button type="button" variant="outline" className="w-full" onClick={() => setPersonalizando(true)}>
              <SlidersHorizontal className="mr-1 h-4 w-4" /> Personalizar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
